import { useReducer } from "react";
import styled from "styled-components";

import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { UserDownturnReaction } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { entryFont } from "@entry/tokens";
import { EntryGlassOption } from "@entry/ui";
import { useErrorBanner, useOnboarding, useSetUserFinancialProfile } from "@hooks";
import { Translations, i18n } from "@i18n";
import { DownturnChart } from "./DownturnChart";
import {
  InvestmentDownturnReactionSelectionAction,
  InvestmentDownturnReactionSelectionState,
  InvestmentDownturnReactionSelectionType,
} from "./types";

const initialState: InvestmentDownturnReactionSelectionState = {
  sell: false,
  hold: false,
  buy: false,
};

const reducer = (
  state: InvestmentDownturnReactionSelectionState,
  action: InvestmentDownturnReactionSelectionAction,
) => {
  return { ...initialState, [action.type]: !state[action.type] };
};

const Disclaimer = styled.p`
  margin: 0;
  font-family: ${entryFont.body};
  font-size: 11px;
  line-height: 13px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
`;

export const InvestmentDownturnReactionRoute = () => {
  const { navigateToNext } = useOnboarding();

  const [setUserFinancialProfile, { loading }] = useSetUserFinancialProfile();

  const { showErrorBanner } = useErrorBanner();

  const [{ sell, hold, buy }, dispatch] = useReducer(reducer, initialState);

  const downturnReaction = (() => {
    if (buy) return UserDownturnReaction.Buy;
    if (hold) return UserDownturnReaction.Hold;
    if (sell) return UserDownturnReaction.Sell;
    return undefined;
  })();

  const handleSubmit = async () => {
    try {
      await setUserFinancialProfile({
        variables: {
          downturnReaction,
        },
      });

      navigateToNext();
    } catch {
      showErrorBanner();
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.InvestmentDownturnReactionRouteTitle),
    subtitle: i18n.t(Translations.InvestmentDownturnReactionRouteSubtitle),
    cta: {
      primary: {
        title: i18n.t(Translations.InvestmentDownturnReactionRoutePrimaryCTA),
        onClick: handleSubmit,
        disabled: !downturnReaction,
        loading,
      },
    },
    footer: (
      <Disclaimer>
        {i18n.t(Translations.InvestmentDownturnReactionRouteDisclaimer)}
      </Disclaimer>
    ),
  };

  const selectSell = () => {
    dispatch({ type: InvestmentDownturnReactionSelectionType.Sell });
  };

  const selectHold = () => {
    dispatch({ type: InvestmentDownturnReactionSelectionType.Hold });
  };

  const selectBuy = () => {
    dispatch({ type: InvestmentDownturnReactionSelectionType.Buy });
  };

  return (
    <OnboardingLayout {...props}>
      <DownturnChart />
      <Spacing size={SpacingSizes.XL} />

      <EntryGlassOption
        title={i18n.t(Translations.InvestmentDownturnReactionRouteSell)}
        selected={sell}
        onSelect={selectSell}
      />
      <Spacing size={SpacingSizes.S} />

      <EntryGlassOption
        title={i18n.t(Translations.InvestmentDownturnReactionRouteHold)}
        selected={hold}
        onSelect={selectHold}
      />
      <Spacing size={SpacingSizes.S} />

      <EntryGlassOption
        title={i18n.t(Translations.InvestmentDownturnReactionRouteBuy)}
        selected={buy}
        onSelect={selectBuy}
      />
      <Spacing size={SpacingSizes.M} />

      <Disclaimer>
        {i18n.t(Translations.InvestmentDownturnReactionRouteDisclaimer)}
      </Disclaimer>
    </OnboardingLayout>
  );
};
